import {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import axios from 'axios';
import {Alert, Box, Stack, Typography} from '@mui/material';
import {
  AttendanceTrackerRegistryRecordError,
  AttendanceTrackerRegistrySession,
} from '../../backend/responses/attendance-tracker-registry';
import {useSnackBar} from '../../contexts/snack-bar/context';
import {translations} from '../../i18n/translations';
import {EntityAttendanceTrackerRegistryRepository} from '../../repositories/entity-attendance-tracker-registry';
import {formatRecordDateTime} from '../../utils/functions/attendance-sessions';
import FlatInput from '../flat-input/flat-input';
import {Modal} from '../modal/modal';

/** Longest reason that can be given (the same as in the backend) */
const REASON_MAX_LENGTH = 300;

type AttendanceRegistryVoidDialogProps = {
  open: boolean;
  onClose: () => void;
  entityId: string;
  session?: AttendanceTrackerRegistrySession;
  /** Called after the session was voided, so the list can be loaded again */
  onVoided: () => void;
};

const SummaryRow = ({label, value}: {label: string; value?: string}) => (
  <Box sx={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
    <Typography sx={{fontSize: '14px', fontWeight: 500, color: 'var(--dcm-gray-general)'}}>
      {label}
    </Typography>
    <Typography sx={{fontSize: '14px', fontWeight: 500, overflowWrap: 'anywhere'}}>
      {value}
    </Typography>
  </Box>
);

/**
 * Voids a session of the registry: it is kept, but it does not count anymore, so that time can be registered
 * again. The reason is asked here, after the user chose to void it in the details of the session.
 */
export default function AttendanceRegistryVoidDialog({
  open,
  onClose,
  entityId,
  session,
  onVoided,
}: AttendanceRegistryVoidDialogProps) {
  const {t} = useTranslation();
  const snackBarContext = useSnackBar();
  const texts = translations.attendance_tracker_registry_screen.void;
  const sessionTexts = translations.daily_care_log.attendance_tracker.session;
  const dailyCareLogTexts = translations.daily_care_log.attendance_tracker;

  const [reason, setReason] = useState('');
  const [hasTriedToVoid, setHasTriedToVoid] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  // The same session is voided only once, even if the request is made again
  const isSavingRef = useRef(false);

  // Starts from scratch every time it is opened
  useEffect(() => {
    if (open) {
      setReason('');
      setHasTriedToVoid(false);
      setError(undefined);
    }
  }, [open]);

  const reasonError = reason.trim().length === 0 ? t(texts.validation_reason_required) : undefined;

  const errorMessage = (e: unknown): string => {
    const code = axios.isAxiosError<AttendanceTrackerRegistryRecordError>(e)
      ? e.response?.data?.code
      : undefined;

    switch (code) {
      case 'conflict':
        return t(texts.error_conflict);
      case 'self-void-not-allowed':
        return t(texts.error_self);
      default:
        return t(texts.error_generic);
    }
  };

  const handleVoid = async () => {
    setHasTriedToVoid(true);
    if (!session || reasonError || isSavingRef.current) {
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);
    setError(undefined);
    try {
      await EntityAttendanceTrackerRegistryRepository.instance.voidSession(
        entityId,
        session.id,
        reason.trim()
      );

      snackBarContext.setOfficeToastNotification({severity: 'success', message: t(texts.success)});
      onVoided();
      onClose();
    } catch (e) {
      console.error(e);
      setError(errorMessage(e));
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  };

  const formatPoint = (point?: AttendanceTrackerRegistrySession['entry']) =>
    point ? formatRecordDateTime(point.registeredAt, point.timeZone).text : undefined;

  return (
    <Modal
      id="attendance-registry-void"
      title={t(texts.title)}
      open={open}
      onClose={onClose}
      preventCloseClick={isSaving}
      actionButton={{type: 'close'}}
      fullWidth
      maxWidth="sm"
      actions={[
        {label: t(texts.cancel), type: 'neutral', disabled: isSaving, onClick: onClose},
        {
          label: t(texts.confirm),
          type: 'destructive',
          variant: 'contained',
          color: 'error',
          loading: isSaving,
          onClick: handleVoid,
        },
      ]}
    >
      {session && (
        <Stack gap="20px">
          {error && <Alert severity="error">{error}</Alert>}

          <Alert severity="warning">{t(texts.description)}</Alert>

          <SummaryRow label={t(dailyCareLogTexts.new.professional)} value={session.user.name} />
          <SummaryRow label={t(sessionTexts.entry)} value={formatPoint(session.entry)} />
          <SummaryRow
            label={t(sessionTexts.exit)}
            value={session.exit ? formatPoint(session.exit) : t(dailyCareLogTexts.details.no_exit)}
          />

          <FlatInput
            label={t(texts.reason)}
            placeholder={t(texts.reason_placeholder)}
            required
            multiline
            minRows={2}
            value={reason}
            onChange={(event) => setReason(event.target.value.slice(0, REASON_MAX_LENGTH))}
            counter={{current: reason.length, max: REASON_MAX_LENGTH}}
            errors={hasTriedToVoid ? reasonError : undefined}
          />
        </Stack>
      )}
    </Modal>
  );
}
